import React, { useEffect, useRef, useState } from "react";
import { api } from "../api.js";

type Sys = Awaited<ReturnType<typeof api.system>>;
const gb = (b: number) => `${(b / 1e9).toFixed(b >= 1e10 ? 0 : 1)} GB`;

/** What the machine running the studio is doing: a small dot and its name in the header; the numbers (CPU, memory, disk, jobs in flight) on a click. */
export const SystemLoad: React.FC = () => {
  const [s, setS] = useState<Sys | null>(null);
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let alive = true;
    const tick = () => api.system().then((x) => { if (alive) setS(x); }).catch(() => undefined);
    tick();
    const t = setInterval(tick, open ? 2000 : 5000);
    return () => { alive = false; clearInterval(t); };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => { if (root.current && !root.current.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("pointerdown", away, true);
    window.addEventListener("keydown", key);
    return () => { window.removeEventListener("pointerdown", away, true); window.removeEventListener("keydown", key); };
  }, [open]);
  if (!s) return null;
  const memUsed = s.memTotal - s.memFree;
  const memPct = Math.round((memUsed / s.memTotal) * 100);
  const hot = s.cpu >= 80 || memPct >= 90, warm = s.cpu >= 50 || memPct >= 75;
  const tone = hot ? "var(--warn)" : warm ? "var(--gold)" : "var(--green)";
  const state = hot ? "working hard" : warm ? "busy" : "fine";
  return (
    <div ref={root} className="sysload" style={{ position: "relative" }}>
      <button className="sysload-btn" onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="dialog" title={`${s.host} is ${state} · click for the numbers`}>
        <span className="dot" style={{ background: tone }} />
        <span className="host">{s.host}</span>
        {s.jobs.length ? <span className="badge">{s.jobs.length}</span> : null}
      </button>
      {open ? (
        <div className="sysload-pop" role="dialog" aria-label={`${s.host}: what it is doing`}>
          <b>{s.host}</b><span className="hint">{s.cores} cores · {state}</span>
          <div className="srow"><span>CPU</span><span className="meter"><i style={{ width: `${Math.min(100, s.cpu)}%`, background: s.cpu >= 80 ? "var(--warn)" : s.cpu >= 50 ? "var(--gold)" : "var(--green)" }} /></span><span className="num">{s.cpu}%</span></div>
          <div className="srow"><span>Load</span><span className="num">{s.load1}% of the cores</span></div>
          <div className="srow"><span>Memory</span><span className="meter"><i style={{ width: `${memPct}%`, background: memPct >= 90 ? "var(--warn)" : "var(--deep-2)" }} /></span><span className="num">{gb(memUsed)} of {gb(s.memTotal)}</span></div>
          <div className="srow"><span>Studio</span><span className="num">{gb(s.studioRss)} in memory</span></div>
          {s.disk ? <div className="srow"><span>Disk</span><span className="num">{gb(s.disk.free)} free of {gb(s.disk.total)}</span></div> : null}
          <div className="srow" style={{ alignItems: "flex-start" }}><span>Jobs</span><span className="num" style={{ whiteSpace: "normal" }}>{s.jobs.length ? s.jobs.map((j) => `${j.type}${j.clipId ? ` ${j.clipId}` : ""} ${Math.round(j.progress * 100)}%`).join(" · ") : "idle"}</span></div>
        </div>
      ) : null}
    </div>
  );
};
